import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import { useGetGracePeriodAbuseStatsQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import CustomGaugeChart from '../../../../../../components/Chart/CustomGaugeChart';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const GAUGE_SEGMENTS = [
  {
    label: 'Very Poor',
    value: 20,
    color: '#c94a6a',
    gradientId: 'grad-very-poor-grace',
    stops: ['#752b3e', '#e66b8b'] as [string, string],
  },
  {
    label: 'Poor',
    value: 20,
    color: '#f2a65a',
    gradientId: 'grad-poor-grace',
    stops: ['#9c6b3a', '#ffbf7a'] as [string, string],
  },
  {
    label: 'Fair',
    value: 20,
    color: '#d6b85a',
    gradientId: 'grad-fair-grace',
    stops: ['#8a763a', '#ebd17a'] as [string, string],
  },
  {
    label: 'Good',
    value: 20,
    color: '#55cfc0',
    gradientId: 'grad-good-grace',
    stops: ['#37877d', '#55cfc0'] as [string, string],
  },
  {
    label: 'Excellent',
    value: 20,
    color: '#5ee07a',
    gradientId: 'grad-excellent-grace',
    stops: ['#3d914f', '#80fc9c'] as [string, string],
  },
];

const GraceTimeAbuse = ({
  department_id,
  office_id,
  filter: allFilters,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const filter = allFilters.graceTimeAbuse;

  const { data: statsData, error } = useGetGracePeriodAbuseStatsQuery({
    start_date: filter.start_date || '',
    end_date: filter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch grace time abuse stats');

  const stats = statsData?.data?.data;
  const score = stats?.average_grace_abuse_percentage || 0;
  const statusLabel = stats?.status || 'N/A';

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full space-y-4 relative">
      <Flex
        justify="space-between"
        align="start"
        className="!z-[10] absolute left-5 right-5 top-5"
      >
        <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight">
          Grace Time Abuse
          <span className="text-theme-primary block text-sm">
            {formatDateRange(filter.start_date, filter.end_date)}
          </span>
        </h4>

        <CustomRangePicker
          startDate={filter.start_date}
          endDate={filter.end_date}
          onChange={(start, end) => {
            setFilter('graceTimeAbuse', { start_date: start, end_date: end });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'day').endOf('day')
          }
          isChanged={isFilterChanged('graceTimeAbuse')}
          onReset={() => resetFilter('graceTimeAbuse')}
        />
      </Flex>
      <div className="h-[70px]" />
      {/* Reusable Gauge Chart - Now handles angle and color internally */}
      <CustomGaugeChart
        score={score}
        direction="reverse"
        statusLabel={statusLabel}
        segments={GAUGE_SEGMENTS}
        showStatusInTooltip={false}
        height={320}
      />
    </div>
  );
};

export default GraceTimeAbuse;
